import React, {useEffect, useState} from 'react';
import {Flex, Form, Input, message, Modal, Upload} from "antd";
import IntlMessage, {IntlMessageText} from "../../../../../components/IntlMessage/IntlMessage";
import classes from "../../Applications/application.module.css";
import FormItem from "antd/es/form/FormItem";
import {useTranslation} from "react-i18next";
import MicroQualificationService from "../../../../../services/MicroQualificationService";
import {useDispatch, useSelector} from "react-redux";
import MyText from "../../../../../components/UI/Text/MyText";
import {changeCurrentPage} from "../../../../../store/slices/tableController/ProfStandardsListTableController";
import BlackerBlueButton from "../../../../../components/UI/Buttons/BlackerBlueButton";
import MyTable from "../../../../../components/Table/MyTable";
import {getAllProfessionalStandards} from "../../../../../store/slices/ProfessionalStandardsSlice";
import GreenButton from "../../../../../components/UI/Buttons/GreenButton";
import BhargaButton from "../../../../../components/UI/Buttons/BhargaButton";
import {UploadOutlined} from "@ant-design/icons";

const ProfessionalStandardsPage = () => {
    const [form] = Form.useForm();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {profStandards, isLoading, hasMore, error} = useSelector(state => state.profStandardsList)
    const {currentPage, pageSize} = useSelector(state => state.profStandardsListTableController);

    const nameRu = Form.useWatch('nameRu', form);
    const nameKz = Form.useWatch('nameKz', form);
    const nameEng = Form.useWatch('nameEng', form);
    const code = Form.useWatch('code', form);
    const date = Form.useWatch('date', form);

    const [isLoadingCreate, setIsLoadingCreate] = useState(false);
    const [openModal, setOpenModal] = useState(false)

    const [uploadedFile, setUploadedFile] = useState(null);

    const columns = [
        {
            title: <MyText center strong><IntlMessage id={"nameRu"}/></MyText>,
            render: (_, record) => (
                <MyText center>{record.nameRu}</MyText>
            ),
            width: '20%',
        },
        {
            title: <MyText center strong><IntlMessage id={"nameKz"}/></MyText>,
            render: (_, record) => (
                <MyText center>{record.nameKz}</MyText>
            ),
            width: '20%',
        },
        {
            title: <MyText center strong><IntlMessage id={"nameEng"}/></MyText>,
            render: (_, record) => (
                <MyText center>{record.nameEng}</MyText>
            ),
            width: '20%',
        },
        {
            title: <MyText center strong><IntlMessage id={"certificate.code"}/></MyText>,
            render: (_, record) => (
                <MyText center>{record.code}</MyText>
            ),
            width: '20',
        },
        {
            title: <MyText center strong><IntlMessage id={"acw.dateOfCompilation"}/></MyText>,
            render: (_, record) => (
                <MyText center>{new Date(record.date).toLocaleDateString()}</MyText>
            ),
            width: '20',
        }

    ];

    function handleCreate() {
        form.validateFields()
            .then(async (values) => {
                setIsLoadingCreate(true);
                setOpenModal(false)
                await MicroQualificationService.saveProfessionalStandard(nameRu, nameKz, nameEng, code, date)
                    .then(() => {
                        message.success(<IntlMessage id={'success'}/>)
                        form.resetFields();
                    })
                    .catch()
                    .finally()
            })
            .catch((error) => {
                console.log("Validation error!", error);
            })
            .finally(() => {
                dispatch(getAllProfessionalStandards({
                    page: currentPage,
                    limit: pageSize
                }))
                setIsLoadingCreate(false);
                message.destroy();
            });
    }

    useEffect(() => {
        dispatch(getAllProfessionalStandards({
            page: currentPage,
            limit: pageSize
        }));
    }, []);

    const fetchData = async (params) => {
        return dispatch(getAllProfessionalStandards({
            ...params
        }));
    }

    const saveCurrentPageSettings = (page, pageSize) => {
        dispatch(changeCurrentPage({page: page, pageSize: pageSize}));
    };

    const handleChangeExcel = (info) => {
        setUploadedFile(info.file);
    };

    const propsExcelFile = {
        beforeUpload: () => false,
        onChange: handleChangeExcel,
        multiple: false,
    };

    const handleImportExcelFile = async () => {
        setIsLoadingCreate(true);
        await MicroQualificationService.importProfessionalStandardsByExcel(uploadedFile)
            .then(() => {
                message.success(<IntlMessage id={'success'}/>)
                setUploadedFile(null);
            })
            .catch()
            .finally(() => {
                dispatch(getAllProfessionalStandards({
                    page: currentPage,
                    limit: pageSize
                }))
                setIsLoadingCreate(false);
                message.destroy();
            })
    }

    return (
        <Flex style={{width: "100%"}} gap={20} align={"flex-start"} justify={"flex-start"} vertical>
            <MyText uppercase strong size={"large"}><IntlMessage id={'profStandards'}/></MyText>
            <Flex gap={"large"} justify={"center"} align={"center"}>
                <BlackerBlueButton onClick={() => {
                    setOpenModal(true)
                }}>
                    <IntlMessage id={'create'}/>
                </BlackerBlueButton>
                <Upload {...propsExcelFile} fileList={uploadedFile ? [uploadedFile] : []}>
                    <GreenButton>
                        <IntlMessage id={'certificate.excelFormat'}/>
                    </GreenButton>
                </Upload>
                {
                    uploadedFile &&
                    <BhargaButton icon={<UploadOutlined/>} onClick={handleImportExcelFile}><IntlMessage id={'upload'}/></BhargaButton>
                }
            </Flex>
            <MyTable
                isLoading={isLoading || isLoadingCreate}
                dataSource={profStandards}
                columns={columns}
                fetchData={fetchData}
                saveCurrentPage={saveCurrentPageSettings}
                initialPage={currentPage}
                initialPageSize={pageSize}
                hasMore={hasMore}
            />
            <Form form={form} className={classes.form} initialValues={{method: 'group'}}>
                <Modal
                    width={500}
                    maskClosable={true}
                    title={<IntlMessage id={'profStandards'}/>}
                    open={openModal}
                    onOk={handleCreate}
                    onCancel={() => {
                        setOpenModal(false)
                    }}
                    okText={<IntlMessage id={'create'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                >
                    <Flex vertical style={{width: '100%'}} gap={4}>
                        <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                  label={t('nameRu')}
                                  labelCol={{span: 24}}
                                  className={classes.formInput} style={{width: '100%', padding: 0}} name="nameRu">
                            <Input rootClassName={classes.formInput}
                                   placeholder={IntlMessageText.getText({id: "nameRu"})}/>
                        </FormItem>
                        <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                  label={t('nameKz')}
                                  labelCol={{span: 24}}
                                  className={classes.formInput} style={{width: '100%', padding: 0}} name="nameKz">
                            <Input rootClassName={classes.formInput}
                                   placeholder={IntlMessageText.getText({id: "nameKz"})}/>
                        </FormItem>
                        <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                  label={t('nameEng')}
                                  labelCol={{span: 24}}
                                  className={classes.formInput} style={{width: '100%', padding: 0}} name="nameEng">
                            <Input rootClassName={classes.formInput}
                                   placeholder={IntlMessageText.getText({id: "nameEng"})}/>
                        </FormItem>
                        <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                  label={t('certificate.code')}
                                  labelCol={{span: 24}}
                                  className={classes.formInput} style={{width: '100%', padding: 0}} name="code">
                            <Input rootClassName={classes.formInput}
                                   placeholder={IntlMessageText.getText({id: "certificate.code"})}/>
                        </FormItem>
                        <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                  label={t('acw.dateOfCompilation')}
                                  labelCol={{span: 24}}
                                  className={classes.formInput} style={{width: '100%', padding: 0}} name="date">
                            <Input type="date" rootClassName={classes.formInput}
                                   placeholder={IntlMessageText.getText({id: "acw.dateOfCompilation"})}/>
                        </FormItem>
                    </Flex>
                </Modal>
            </Form>
        </Flex>
    );
};

export default ProfessionalStandardsPage;